import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import TrainingGuide from "../common/TrainingGuide";
import marketplaceSteps from "../../data/training/marketplace";
import { BRAND } from "../../branding";
import grids from "../responsive-fixes/grids.module.css";

// ── Types ──────────────────────────────────────────────────────────────────────

interface TierConfig {
  rate: number;
  min_volume: number;
  days_required: number;
  requires_certification: boolean;
  requires_product_mix: boolean;
  cross_border: boolean;
  mdf_eligible: boolean;
  label: string;
  color: string;
}

interface Seller {
  id: string;
  name: string;
  business_name?: string;
  tier: string;
  region: string;
  sales_volume: number;
  monthly_sales: number;
  total_commissions: number;
  pending_commissions: number;
  is_active: boolean;
  last_order_at?: string;
}

interface SellersList {
  sellers: Seller[];
  count: number;
}

interface OnboardingStep {
  step: number;
  title: string;
  description: string;
  owner: string;
  auto: boolean;
  status: "completed" | "current" | "pending";
}

interface OnboardingState {
  seller_id: string;
  seller_name: string;
  onboarding_status: string;
  current_step: number;
  total_steps: number;
  progress_pct: number;
  completed_at: string | null;
  notes: string | null;
  steps: OnboardingStep[];
}

// ── Capability cards config ────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    icon: "🏆",
    title: "4-Tier Commission System",
    desc: "Bronze → Silver → Gold → Diamond with auto-upgrade. Rates from 5% to 15% + MDF eligibility at Diamond.",
    color: BRAND.colors.warning,
    endpoint: "GET /api/marketplace/tiers/config",
  },
  {
    icon: "📦",
    title: "Product Serialization",
    desc: "Unit-level serial number registry for Enzo & Lizze products. Gray-market geofence detection across GCC regions.",
    color: BRAND.colors.accent,
    endpoint: "GET /api/marketplace/products/validate",
  },
  {
    icon: "🤖",
    title: "Veridian Ginie AI",
    desc: "AI-powered account prioritization, cross-sell recommendations, commission optimization, and hybrid human-AI task routing.",
    color: BRAND.colors.primary,
    endpoint: "GET /api/marketplace/ai/prioritize",
  },
  {
    icon: "💸",
    title: "Commission Ledger",
    desc: "Full audit trail: pending → approved → paid. Seller dashboards with real-time tier progress and order history.",
    color: BRAND.colors.info,
    endpoint: "GET /api/marketplace/commissions/{seller_id}",
  },
];

const TIER_ORDER = ["bronze", "silver", "gold", "diamond"];

const TIER_BADGE: Record<string, string> = {
  bronze:  "#cd7f32",
  silver:  "#c0c0c0",
  gold:    "#ffd700",
  diamond: "#b9f2ff",
};

// ── Component ──────────────────────────────────────────────────────────────────

const MarketplaceDashboard: React.FC = () => {
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [tiers, setTiers]       = useState<Record<string, TierConfig> | null>(null);
  const [sellers, setSellers]   = useState<SellersList | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "sellers" | "tiers" | "onboarding">("overview");

  // Onboarding lookup state
  const [onbSellerId, setOnbSellerId] = useState("");
  const [onbState, setOnbState]       = useState<OnboardingState | null>(null);
  const [onbLoading, setOnbLoading]   = useState(false);
  const [onbError, setOnbError]       = useState<string | null>(null);
  const [onbMsg, setOnbMsg]           = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [tiersData, sellersData] = await Promise.all([
          api.marketplace.tierConfig(),
          api.marketplace.sellers(),
        ]);
        setTiers(tiersData.tiers);
        setSellers(sellersData);
      } catch (e: any) {
        setError(e.message || "Failed to load marketplace data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadOnboarding = useCallback(async () => {
    if (!onbSellerId.trim()) return;
    setOnbLoading(true);
    setOnbError(null);
    setOnbState(null);
    try {
      const data = await api.marketplace.onboarding(onbSellerId.trim());
      setOnbState(data);
    } catch (e: any) {
      setOnbError(e.message || "Seller not found");
    } finally {
      setOnbLoading(false);
    }
  }, [onbSellerId]);

  const completeStep = async (step: number) => {
    if (!onbState) return;
    setOnbMsg(null);
    try {
      const res = await api.marketplace.completeOnboardingStep(onbState.seller_id, step);
      setOnbMsg(res.onboarding_complete ? "Onboarding complete!" : `Step ${step} marked done`);
      await loadOnboarding();
    } catch (e: any) {
      setOnbMsg(`Error: ${e.message}`);
    }
  };

  // ── Tier distribution from seller list ──────────────────────────────────────
  const tierCounts = sellers
    ? TIER_ORDER.reduce((acc, t) => {
        acc[t] = sellers.sellers.filter(s => s.tier === t).length;
        return acc;
      }, {} as Record<string, number>)
    : null;

  return (
    <div style={{ padding: "24px 28px", fontFamily: BRAND.fontFamily, color: "#dde5ff" }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 28 }}>🏪</span>
          <h1 className="page-title" style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
            Marketplace
          </h1>
          <span style={{
            background: `${BRAND.colors.accent}26`,
            border: `1px solid ${BRAND.colors.accent}4d`,
            color: BRAND.colors.accent,
            borderRadius: 6,
            padding: "2px 10px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
          }}>
            v1.0 · LIVE
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ margin: 0, color: BRAND.colors.textMuted, fontSize: 13 }}>
            Affiliate & distributor portal for Enzo Italy and Lizze Professional — UAE + GCC
          </p>
          <button onClick={() => setTrainingOpen(true)} title="Open training guide" style={{ background: `${BRAND.colors.warning}1a`, border: `1px solid ${BRAND.colors.warning}33`, borderRadius: 8, color: BRAND.colors.warning, cursor: "pointer", fontSize: 14, fontWeight: 700, padding: "3px 10px", flexShrink: 0 }}>?</button>
        </div>
      </div>
      <TrainingGuide moduleKey="marketplace" steps={marketplaceSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid rgba(189,178,255,0.12)", paddingBottom: 0 }}>
        {(["overview", "sellers", "tiers", "onboarding"] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none",
              border: "none",
              color: activeTab === tab ? BRAND.colors.primary : BRAND.colors.textMuted,
              borderBottom: activeTab === tab ? `2px solid ${BRAND.colors.primary}` : "2px solid transparent",
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: activeTab === tab ? 700 : 500,
              cursor: "pointer",
              marginBottom: -1,
              textTransform: "capitalize",
              fontFamily: BRAND.fontFamily,
            }}
          >
            {tab === "overview" ? "Overview" : tab === "sellers" ? `Sellers (${sellers?.count ?? "—"})` : tab === "tiers" ? "Tier Config" : "Onboarding"}
          </button>
        ))}
      </div>

      {error && (
        <div style={{
          background: `${BRAND.colors.danger}1a`,
          border: `1px solid ${BRAND.colors.danger}4d`,
          borderRadius: 10,
          padding: "12px 16px",
          color: BRAND.colors.danger,
          fontSize: 13,
          marginBottom: 20,
        }}>
          ⚠️ {error} — backend may need the marketplace schema migrated
        </div>
      )}

      {/* ── OVERVIEW TAB ─────────────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <>
          {/* Capability cards — M2 fix (audit): 280px overflow on narrow phones. */}
          <div className={grids["responsive-grid"]} style={{ gap: 16, marginBottom: 28 }}>
            {CAPABILITIES.map(cap => (
              <div key={cap.title} style={{
                background: BRAND.glassEffect,
                border: "1px solid rgba(189,178,255,0.12)",
                borderRadius: BRAND.borderRadius,
                padding: "20px",
                backdropFilter: BRAND.blur,
                WebkitBackdropFilter: BRAND.blur,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 22 }}>{cap.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: cap.color }}>{cap.title}</span>
                </div>
                <p style={{ margin: "0 0 12px", fontSize: 12, color: BRAND.colors.textMuted, lineHeight: 1.6 }}>{cap.desc}</p>
                <code style={{
                  fontSize: 10,
                  color: BRAND.colors.textMuted,
                  background: "rgba(6,14,32,0.5)",
                  padding: "4px 8px",
                  borderRadius: 4,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {cap.endpoint}
                </code>
              </div>
            ))}
          </div>

          {/* API Endpoint reference */}
          <div style={{
            background: BRAND.glassEffect,
            border: "1px solid rgba(189,178,255,0.12)",
            borderRadius: BRAND.borderRadius,
            padding: "20px 24px",
            backdropFilter: BRAND.blur,
            WebkitBackdropFilter: BRAND.blur,
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 14, color: BRAND.colors.primary, fontWeight: 700 }}>
              Backend Endpoints — /api/marketplace
            </h3>
            {/* M2 fix (audit): 320px endpoint grid clipped at <320px viewport. */}
            <div className={grids["responsive-grid"]} style={{ gap: 6 }}>
              {[
                ["POST", "/sellers/register",               "Register new affiliate/seller"],
                ["GET",  "/sellers",                        "List all active sellers"],
                ["GET",  "/sellers/{id}/dashboard",         "Full seller dashboard"],
                ["GET",  "/sellers/{id}/tier",              "Tier & upgrade progress"],
                ["GET",  "/tiers/config",                   "All tier configs"],
                ["POST", "/orders",                         "Create marketplace order"],
                ["GET",  "/orders",                         "List orders"],
                ["PATCH","/orders/{id}/status",             "Update order status"],
                ["GET",  "/commissions/{seller_id}",        "Commission history"],
                ["PATCH","/commissions/{id}/approve",       "Approve commission"],
                ["PATCH","/commissions/{id}/pay",           "Mark commission paid"],
                ["POST", "/products/register",              "Register serial number"],
                ["GET",  "/products/validate?serial=…",     "Validate serial"],
                ["POST", "/products/geofence",              "Gray-market geofence check"],
                ["GET",  "/ai/prioritize",                  "Veridian Ginie: top accounts"],
                ["GET",  "/ai/cross-sells/{seller_id}",     "Cross-sell recommendations"],
                ["GET",  "/ai/commission-optimize",         "Commission optimization AI"],
                ["POST", "/ai/route-task",                  "Hybrid AI-human task router"],
                ["GET",  "/portal",                         "Standalone affiliate portal (HTML)"],
              ].map(([method, path, label]) => (
                <div key={path} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                  <span style={{
                    background: method === "GET" ? `${BRAND.colors.accent}26` : method === "POST" ? `${BRAND.colors.info}26` : `${BRAND.colors.warning}26`,
                    color: method === "GET" ? BRAND.colors.accent : method === "POST" ? BRAND.colors.info : BRAND.colors.warning,
                    borderRadius: 4,
                    padding: "1px 6px",
                    fontWeight: 700,
                    minWidth: 40,
                    textAlign: "center",
                    fontSize: 10,
                  }}>{method}</span>
                  <code style={{ color: BRAND.colors.textMuted, flex: 1 }}>{path}</code>
                  <span style={{ color: BRAND.colors.textMuted }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── SELLERS TAB ──────────────────────────────────────────────────────── */}
      {activeTab === "sellers" && (
        <div>
          {/* Tier distribution summary */}
          {tierCounts && (
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              {TIER_ORDER.map(t => (
                <div key={t} style={{
                  background: BRAND.glassEffect,
                  border: `1px solid ${TIER_BADGE[t]}33`,
                  borderRadius: BRAND.borderRadius,
                  padding: "12px 20px",
                  textAlign: "center",
                  flex: 1,
                  backdropFilter: BRAND.blur,
                  WebkitBackdropFilter: BRAND.blur,
                }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: TIER_BADGE[t] }}>{tierCounts[t]}</div>
                  <div style={{ fontSize: 11, color: BRAND.colors.textMuted, textTransform: "capitalize", marginTop: 2 }}>{t}</div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ color: BRAND.colors.textMuted, padding: 40, textAlign: "center" }}>Loading sellers…</div>
          ) : sellers && sellers.sellers.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sellers.sellers.map(s => (
                <div key={s.id} style={{
                  background: BRAND.glassEffect,
                  border: "1px solid rgba(189,178,255,0.12)",
                  borderRadius: BRAND.borderRadius,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  backdropFilter: BRAND.blur,
                  WebkitBackdropFilter: BRAND.blur,
                }}>
                  <span style={{
                    background: TIER_BADGE[s.tier] + "22",
                    color: TIER_BADGE[s.tier],
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "capitalize",
                    minWidth: 60,
                    textAlign: "center",
                  }}>{s.tier}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#dde5ff" }}>{s.name}</div>
                    {s.business_name && <div style={{ fontSize: 11, color: BRAND.colors.textMuted }}>{s.business_name}</div>}
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12, color: BRAND.colors.textMuted }}>
                    <div>AED {s.sales_volume.toLocaleString()} total</div>
                    <div style={{ color: BRAND.colors.textMuted, fontSize: 11 }}>AED {s.monthly_sales.toLocaleString()} / mo</div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12 }}>
                    <div style={{ color: BRAND.colors.accent }}>AED {s.pending_commissions.toFixed(2)} pending</div>
                    <div style={{ color: BRAND.colors.textMuted, fontSize: 11 }}>{s.region?.toUpperCase()}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              background: BRAND.glassEffect,
              border: "1px solid rgba(189,178,255,0.12)",
              borderRadius: BRAND.borderRadius,
              padding: "48px 24px",
              textAlign: "center",
              color: BRAND.colors.textMuted,
              backdropFilter: BRAND.blur,
              WebkitBackdropFilter: BRAND.blur,
            }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🏪</div>
              <div style={{ fontSize: 14, marginBottom: 6 }}>No sellers registered yet</div>
              <div style={{ fontSize: 12 }}>
                Use <code style={{ color: BRAND.colors.primary }}>POST /api/marketplace/sellers/register</code> to add the first affiliate
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TIERS TAB ────────────────────────────────────────────────────────── */}
      {activeTab === "tiers" && (
        // M2 fix (audit): 240px tiers grid pre-collapses on narrow phones.
        <div className={grids["responsive-grid"]} style={{ gap: 16 }}>
          {loading ? (
            <div style={{ color: BRAND.colors.textMuted, padding: 40 }}>Loading tier config…</div>
          ) : tiers ? (
            TIER_ORDER.map(key => {
              const t = tiers[key];
              if (!t) return null;
              return (
                <div key={key} style={{
                  background: BRAND.glassEffect,
                  border: `1px solid ${t.color}44`,
                  borderRadius: BRAND.borderRadius,
                  padding: "22px",
                  backdropFilter: BRAND.blur,
                  WebkitBackdropFilter: BRAND.blur,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: t.color }}>{t.label}</span>
                    <span style={{
                      background: t.color + "22",
                      color: t.color,
                      borderRadius: 8,
                      padding: "4px 12px",
                      fontSize: 16,
                      fontWeight: 800,
                    }}>{(t.rate * 100).toFixed(0)}%</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: BRAND.colors.textMuted }}>
                    <div>
                      <span style={{ color: BRAND.colors.textMuted }}>Min volume: </span>
                      <span style={{ color: "#dde5ff", fontWeight: 600 }}>
                        {t.min_volume > 0 ? `AED ${t.min_volume.toLocaleString()}` : "None"}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: BRAND.colors.textMuted }}>Days active: </span>
                      <span style={{ color: "#dde5ff", fontWeight: 600 }}>{t.days_required || "None"}</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                      {t.requires_certification && (
                        <span style={{ background: `${BRAND.colors.warning}26`, color: BRAND.colors.warning, borderRadius: 4, padding: "2px 7px", fontSize: 10 }}>
                          Certification required
                        </span>
                      )}
                      {t.requires_product_mix && (
                        <span style={{ background: `${BRAND.colors.accent}26`, color: BRAND.colors.accent, borderRadius: 4, padding: "2px 7px", fontSize: 10 }}>
                          Product mix compliance
                        </span>
                      )}
                      {t.mdf_eligible && (
                        <span style={{ background: `${BRAND.colors.primary}26`, color: BRAND.colors.primary, borderRadius: 4, padding: "2px 7px", fontSize: 10 }}>
                          MDF eligible
                        </span>
                      )}
                      {t.cross_border && (
                        <span style={{ background: `${BRAND.colors.info}26`, color: BRAND.colors.info, borderRadius: 4, padding: "2px 7px", fontSize: 10 }}>
                          Cross-border
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ color: BRAND.colors.textMuted }}>Could not load tier config</div>
          )}
        </div>
      )}
      {/* ── ONBOARDING TAB ───────────────────────────────────────────────── */}
      {activeTab === "onboarding" && (
        <div>
          {/* Lookup bar */}
          <div style={{
            display: "flex", gap: 10, marginBottom: 24,
            background: BRAND.glassEffect,
            border: "1px solid rgba(189,178,255,0.12)",
            borderRadius: BRAND.borderRadius, padding: "14px 16px",
            backdropFilter: BRAND.blur,
            WebkitBackdropFilter: BRAND.blur,
          }}>
            <input
              value={onbSellerId}
              onChange={e => setOnbSellerId(e.target.value)}
              onKeyDown={e => e.key === "Enter" && loadOnboarding()}
              placeholder="Enter Seller ID (UUID)…"
              style={{
                flex: 1, background: "rgba(6,14,32,0.6)", border: "1px solid rgba(189,178,255,0.2)",
                borderRadius: 8, padding: "8px 12px", color: "#dde5ff", fontSize: 13, outline: "none",
                fontFamily: BRAND.fontFamily,
              }}
            />
            <button
              onClick={loadOnboarding}
              style={{
                background: `linear-gradient(135deg,${BRAND.colors.accent},${BRAND.colors.primary})`,
                border: "none", borderRadius: 8, padding: "8px 20px",
                color: BRAND.colors.secondary, fontWeight: 700, fontSize: 13, cursor: "pointer",
                fontFamily: BRAND.fontFamily,
              }}
            >
              {onbLoading ? "Loading…" : "Load"}
            </button>
          </div>

          {onbError && (
            <div style={{
              background: `${BRAND.colors.danger}1a`, border: `1px solid ${BRAND.colors.danger}4d`,
              borderRadius: BRAND.borderRadius, padding: "12px 16px", color: BRAND.colors.danger, fontSize: 13, marginBottom: 16,
            }}>⚠️ {onbError}</div>
          )}

          {onbMsg && (
            <div style={{
              background: `${BRAND.colors.accent}1a`, border: `1px solid ${BRAND.colors.accent}4d`,
              borderRadius: BRAND.borderRadius, padding: "12px 16px", color: BRAND.colors.accent, fontSize: 13, marginBottom: 16,
            }}>✅ {onbMsg}</div>
          )}

          {onbState ? (
            <div>
              {/* Header */}
              <div style={{
                background: BRAND.glassEffect, border: "1px solid rgba(189,178,255,0.12)",
                borderRadius: BRAND.borderRadius, padding: "20px 24px", marginBottom: 20,
                backdropFilter: BRAND.blur,
                WebkitBackdropFilter: BRAND.blur,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: BRAND.colors.warning, marginBottom: 4 }}>{onbState.seller_name}</div>
                    <div style={{ fontSize: 12, color: BRAND.colors.textMuted }}>ID: {onbState.seller_id}</div>
                  </div>
                  <span style={{
                    background: onbState.onboarding_status === "completed"
                      ? `${BRAND.colors.accent}26` : onbState.onboarding_status === "in_progress"
                      ? `${BRAND.colors.warning}26` : `${BRAND.colors.textMuted}26`,
                    color: onbState.onboarding_status === "completed" ? BRAND.colors.accent
                      : onbState.onboarding_status === "in_progress" ? BRAND.colors.warning : BRAND.colors.textMuted,
                    borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 1,
                  }}>
                    {onbState.onboarding_status.replace("_", " ")}
                  </span>
                </div>

                {/* Progress bar */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: BRAND.colors.textMuted, marginBottom: 6 }}>
                    <span>Step {onbState.current_step} of {onbState.total_steps}</span>
                    <span>{onbState.progress_pct}%</span>
                  </div>
                  <div style={{ height: 6, background: "rgba(189,178,255,0.12)", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${onbState.progress_pct}%`,
                      background: `linear-gradient(90deg,${BRAND.colors.accent},${BRAND.colors.primary})`,
                      borderRadius: 6,
                      transition: "width 0.5s ease",
                    }} />
                  </div>
                </div>

                {onbState.completed_at && (
                  <div style={{ fontSize: 11, color: BRAND.colors.textMuted, marginTop: 8 }}>
                    Completed: {new Date(onbState.completed_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Steps */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {onbState.steps.map(step => (
                  <div key={step.step} style={{
                    background: BRAND.glassEffect,
                    border: step.status === "current"
                      ? `1px solid ${BRAND.colors.accent}66`
                      : "1px solid rgba(189,178,255,0.12)",
                    borderRadius: BRAND.borderRadius,
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    opacity: step.status === "pending" ? 0.5 : 1,
                    backdropFilter: BRAND.blur,
                    WebkitBackdropFilter: BRAND.blur,
                  }}>
                    {/* Step circle */}
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: step.status === "completed" ? `${BRAND.colors.accent}33`
                        : step.status === "current" ? `${BRAND.colors.accent}33`
                        : `${BRAND.colors.textMuted}26`,
                      color: step.status === "completed" ? BRAND.colors.accent
                        : step.status === "current" ? BRAND.colors.accent : BRAND.colors.textMuted,
                      fontWeight: 800, fontSize: 14,
                    }}>
                      {step.status === "completed" ? "✓" : step.step}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#dde5ff", marginBottom: 3 }}>{step.title}</div>
                      <div style={{ fontSize: 11, color: BRAND.colors.textMuted }}>{step.description}</div>
                      <div style={{ fontSize: 10, color: BRAND.colors.textMuted, marginTop: 4 }}>
                        Owner: <span style={{ color: BRAND.colors.textMuted }}>{step.owner}</span>
                        {step.auto && <span style={{ marginLeft: 8, color: BRAND.colors.textMuted }}>· auto</span>}
                      </div>
                    </div>

                    {/* Action */}
                    {step.status === "current" && !step.auto && (
                      <button
                        onClick={() => completeStep(step.step)}
                        style={{
                          background: `linear-gradient(135deg,${BRAND.colors.accent},${BRAND.colors.primary})`,
                          border: "none", borderRadius: 8, padding: "6px 16px",
                          color: BRAND.colors.secondary, fontWeight: 700, fontSize: 12, cursor: "pointer",
                          flexShrink: 0,
                          fontFamily: BRAND.fontFamily,
                        }}
                      >
                        Mark Done
                      </button>
                    )}
                    {step.status === "completed" && (
                      <span style={{ fontSize: 11, color: BRAND.colors.accent, flexShrink: 0 }}>Done</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : !onbLoading && (
            <div style={{
              textAlign: "center", color: BRAND.colors.textMuted, padding: "48px 24px",
              background: BRAND.glassEffect, borderRadius: BRAND.borderRadius,
              border: "1px solid rgba(189,178,255,0.12)",
              backdropFilter: BRAND.blur,
              WebkitBackdropFilter: BRAND.blur,
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🎓</div>
              <div style={{ fontSize: 14, marginBottom: 6 }}>Enter a Seller ID to view their onboarding progress</div>
              <div style={{ fontSize: 12, color: BRAND.colors.textMuted }}>
                Onboarding starts automatically when a seller registers
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketplaceDashboard;
